import { create } from 'zustand';
import { targetApi } from '../services/api';
import { normalizeGeometry } from '../lib/geo';
import type { Target, TargetStatus } from '../types/domain';

// Defensive adapter: fold whatever geometry shape A returns onto our canonical Target.
export function normalizeTarget(raw: any): Target {
  const geom = normalizeGeometry(raw);
  return {
    ...raw,
    shape: geom?.shape ?? 'POINT',
    centerLat: geom?.centerLat ?? null,
    centerLon: geom?.centerLon ?? null,
    radiusMeters: geom?.radiusMeters ?? null,
    coords: geom?.coords ?? null,
    assignments: raw.assignments ?? [],
  } as Target;
}

interface TargetState {
  targets: Target[];
  includeExpired: boolean;
  isLoading: boolean;
  error: string | null;

  fetchTargets: (params?: { unitId?: string; status?: string }) => Promise<void>;
  setIncludeExpired: (v: boolean) => void;
  upsertTarget: (t: Target) => void;
  applyStatus: (id: string, status: TargetStatus) => void;
  removeTarget: (id: string) => void;
  getById: (id: string) => Target | undefined;
}

export const useTargetStore = create<TargetState>((set, get) => ({
  targets: [],
  includeExpired: false,
  isLoading: false,
  error: null,

  fetchTargets: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await targetApi.getAll({ ...params, includeExpired: get().includeExpired });
      const list = (res.data.targets || []).map(normalizeTarget);
      set({ targets: list, isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to fetch targets', isLoading: false });
    }
  },

  setIncludeExpired: (v) => set({ includeExpired: v }),

  upsertTarget: (t) =>
    set((state) => {
      const target = normalizeTarget(t);
      const exists = state.targets.some((x) => x.id === target.id);
      return {
        targets: exists
          ? state.targets.map((x) => (x.id === target.id ? { ...x, ...target } : x))
          : [...state.targets, target],
      };
    }),

  applyStatus: (id, status) =>
    set((state) => ({
      targets: state.targets.map((x) => (x.id === id ? { ...x, status } : x)),
    })),

  removeTarget: (id) => set((state) => ({ targets: state.targets.filter((x) => x.id !== id) })),

  getById: (id) => get().targets.find((x) => x.id === id),
}));
